import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { isWhitelabelBrand, useBranding } from 'modules/App/hooks/useBranding';
import WhitelabelUpgradeContact from 'modules/Common/ui/WhitelabelUpgradeContact';
// Same Calendly product-demo event TrialExpiredPage/PaymentPage already open
// for a call with Victor (CEO) — reused here rather than a new literal. CG
// brand only — see the whitelabel branch below (fixpack item 17).
import { CALENDLY_PRODUCT_DEMO_URL } from 'modules/Onboarding/utils/constants';

// Builder (not a static array) so every feature label routes through i18n.
// `id` is the React key — the label itself is now locale-dependent so it can
// no longer double as a stable key (was `f.label` before this pass).
const buildFeatures = (t) => [
  {
    id: 'centralize',
    label: t(
      'freeSupplier:upgradePlans.features.centralize',
      'Centralize operator, installation & factor data',
    ),
    free: true,
  },
  {
    id: 'portal',
    label: t('freeSupplier:upgradePlans.features.portal', 'Public CBAM portal & share links'),
    free: true,
  },
  {
    id: 'templateParsing',
    label: t(
      'freeSupplier:upgradePlans.features.templateParsing',
      'Communication template parsing & diagrams',
    ),
    free: true,
  },
  {
    id: 'dataRequests',
    label: t(
      'freeSupplier:upgradePlans.features.dataRequests',
      'Data requests with declarants & operators',
    ),
    free: true,
  },
  {
    id: 'widget',
    label: t('freeSupplier:upgradePlans.features.widget', 'Embeddable website widget'),
    free: true,
  },
  {
    id: 'sefa',
    label: t(
      'freeSupplier:upgradePlans.features.sefa',
      'SEFA / emissions calculation from activity data',
    ),
    free: false,
  },
  {
    id: 'cbamCosts',
    label: t(
      'freeSupplier:upgradePlans.features.cbamCosts',
      'CBAM cost estimates & certificate price tracking',
    ),
    free: false,
  },
  {
    id: 'verification',
    label: t(
      'freeSupplier:upgradePlans.features.verification',
      'Verification support & verified portal badge',
    ),
    free: false,
  },
  {
    id: 'insights',
    label: t('freeSupplier:upgradePlans.features.insights', 'Data insights & benchmarking'),
    free: false,
  },
  {
    id: 'monitoringPlan',
    label: t(
      'freeSupplier:upgradePlans.features.monitoringPlan',
      'Monitoring plan & compliance reports',
    ),
    free: false,
  },
];

// NO Stripe / payment wiring here by design — this CTA only opens a Calendly
// booking link in a new tab. Pricing is billed manually after the call.
const openBookACall = () => window.open(CALENDLY_PRODUCT_DEMO_URL, '_blank', 'noopener,noreferrer');

export default function UpgradePlansPage() {
  const { t } = useTranslation('freeSupplier');
  const brand = useBranding();
  const isCarbonGlance = !isWhitelabelBrand(brand);
  const FEATURES = buildFeatures(t);

  return (
    <div className="flex flex-col gap-6 max-w-3xl w-full mx-auto">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-800 m-0">
          {t('freeSupplier:upgradePlans.title', 'Compare plans')}
        </h1>
        <p className="text-sm text-slate-500 mt-1 m-0">
          {t(
            'freeSupplier:upgradePlans.subtitle',
            'Free is for sharing the data you already have. Premium calculates, verifies and keeps you compliant.',
          )}
        </p>
      </div>

      {!isCarbonGlance ? (
        <WhitelabelUpgradeContact />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            {/* Free */}
            <div className="rounded-2xl bg-white border border-slate-200 p-6 flex flex-col">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 m-0">
                {t('freeSupplier:upgradePlans.free.badge', 'Free')}
              </p>
              <p className="text-3xl font-bold text-slate-800 m-0 mt-1">
                £0
                <span className="text-sm font-normal text-slate-400">
                  {' '}
                  / {t('freeSupplier:upgradePlans.free.period', 'forever')}
                </span>
              </p>
              <p className="text-xs text-slate-500 m-0 mt-1">
                {t('freeSupplier:upgradePlans.free.tagline', 'Centralize & share your CBAM data.')}
              </p>
              <ul className="list-none p-0 m-0 mt-4 flex flex-col gap-2 flex-1">
                {FEATURES.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-start gap-2 text-xs"
                  >
                    <FontAwesomeIcon
                      icon={f.free ? faCheck : faXmark}
                      className={`mt-0.5 shrink-0 ${f.free ? 'text-primaryColor' : 'text-slate-300'}`}
                    />
                    <span className={f.free ? 'text-slate-600' : 'text-slate-400'}>{f.label}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 text-center text-xs font-semibold text-slate-400 border border-slate-200 rounded-xl py-2">
                {t('freeSupplier:upgradePlans.free.currentPlan', 'Your current plan')}
              </div>
            </div>

            {/* Premium */}
            <div className="rounded-2xl bg-white border-2 border-primaryColor p-6 flex flex-col relative overflow-hidden">
              <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider text-white bg-primaryColor rounded-full px-2 py-0.5">
                {t('freeSupplier:upgradePlans.premium.recommended', 'Recommended')}
              </span>
              <p className="text-[10px] font-bold uppercase tracking-wider text-primaryColor m-0">
                {t('freeSupplier:upgradePlans.premium.badge', 'Premium')}
              </p>
              <p className="text-3xl font-bold text-slate-800 m-0 mt-1">
                £2,500
                <span className="text-sm font-normal text-slate-400">
                  {' '}
                  / {t('freeSupplier:upgradePlans.premium.period', 'installation / year')}
                </span>
              </p>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                {t(
                  'freeSupplier:upgradePlans.premium.billingNote',
                  'GBP, billed yearly per installation',
                )}
              </p>
              <p className="text-xs text-slate-500 m-0 mt-2">
                {t(
                  'freeSupplier:upgradePlans.premium.tagline',
                  'Everything in Free, plus calculation, verification & compliance.',
                )}
              </p>
              <ul className="list-none p-0 m-0 mt-4 flex flex-col gap-2 flex-1">
                {FEATURES.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-start gap-2 text-xs"
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="mt-0.5 shrink-0 text-primaryColor"
                    />
                    <span className="text-slate-600">{f.label}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5">
                <CgRadixButton
                  variant="primary"
                  size="lg"
                  icon={faCalendarAlt}
                  className="w-full justify-center"
                  onClick={openBookACall}
                >
                  {t('freeSupplier:upgradePlans.premium.bookCall', 'Book a call')}
                </CgRadixButton>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 text-center m-0">
            {t(
              'freeSupplier:upgradePlans.walkthroughFooter',
              'Prefer a walkthrough? Our team can calculate your first SEFA with you on a call.',
            )}
          </p>
        </>
      )}
    </div>
  );
}
